import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  getOrderDetail, 
  updateOrderStatus, 
  addOrderNotes,
  resolveDispute 
} from '../../services/api';

function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [disputeResolution, setDisputeResolution] = useState({
    resolution: '',
    refund_amount: 0,
    resolution_notes: ''
  });

  useEffect(() => {
    fetchOrderDetail();
  }, [orderId]);

  const fetchOrderDetail = async () => {
    try {
      setLoading(true);
      const response = await getOrderDetail(orderId);
      setOrder(response.data);
    } catch (err) {
      setError('Failed to load order details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      setError('Please select a status');
      return;
    }

    try {
      setActionLoading(true);
      await updateOrderStatus(orderId, { 
        status: newStatus, 
        admin_notes: `Status changed to ${newStatus}` 
      });
      setShowStatusModal(false);
      setNewStatus('');
      await fetchOrderDetail();
    } catch (err) {
      setError(`Failed to update status: ` + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNotes = async () => {
    if (!notes.trim()) {
      setError('Please enter notes');
      return;
    }

    try {
      setActionLoading(true);
      await addOrderNotes(orderId, { notes: notes.trim() });
      setShowNotesModal(false);
      setNotes('');
      await fetchOrderDetail();
    } catch (err) {
      setError(`Failed to add notes: ` + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisputeResolution = async () => {
    if (!disputeResolution.resolution) {
      setError('Please select a resolution type');
      return;
    }

    if (disputeResolution.resolution === 'partial_refund' && !disputeResolution.refund_amount) {
      setError('Please enter refund amount for partial refund');
      return;
    }

    try {
      setActionLoading(true);
      await resolveDispute(orderId, disputeResolution);
      setShowDisputeModal(false);
      setDisputeResolution({
        resolution: '',
        refund_amount: 0,
        resolution_notes: ''
      });
      await fetchOrderDetail();
    } catch (err) {
      setError(`Failed to resolve dispute: ` + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 text-sm font-medium rounded-full";
    
    switch (status) {
      case 'pending':
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Pending</span>;
      case 'offer_pending':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>Offer Pending</span>;
      case 'accepted':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Accepted</span>;
      case 'rejected':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>Rejected</span>;
      case 'cancelled':
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Cancelled</span>;
      case 'completed':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Completed</span>;
      case 'shipped':
        return <span className={`${baseClasses} bg-purple-100 text-purple-800`}>Shipped</span>;
      case 'delivered':
        return <span className={`${baseClasses} bg-indigo-100 text-indigo-800`}>Delivered</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{status}</span>;
    }
  };

  const getEscrowBadge = (escrowStatus) => {
    if (!escrowStatus) return null;
    
    const baseClasses = "px-3 py-1 text-sm font-medium rounded-full";
    
    switch (escrowStatus) {
      case 'held':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>💰 Funds Held</span>;
      case 'released':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>✅ Funds Released</span>;
      case 'refunded':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>↩️ Funds Refunded</span>;
      case 'partially_released':
        return <span className={`${baseClasses} bg-orange-100 text-orange-800`}>⏸️ Partially Released</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Escrow: {escrowStatus}</span>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Order</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => navigate('/admin/orders')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            ← Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Link 
                to="/admin/orders"
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                ← Back to Orders
              </Link>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
            <p className="text-gray-600 mt-1">Order #{order?.order_id} • {order?.listing_title}</p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={() => setShowNotesModal(true)}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Add Notes
            </button>
            <button
              onClick={() => setShowStatusModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Update Status
            </button>
            {order?.disputed && (
              <button
                onClick={() => setShowDisputeModal(true)}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Resolve Dispute
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-red-500">⚠️</span>
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-700 hover:text-red-800"
          >
            ✕
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Overview */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">{order?.listing_title}</h2>
                <div className="flex items-center space-x-3">
                  {getStatusBadge(order?.status)}
                  {order?.disputed && (
                    <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
                      ⚠️ Disputed
                    </span>
                  )}
                  {getEscrowBadge(order?.escrow_status)}
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(order?.total_amount)}
                </p>
                <p className="text-sm text-gray-500">Total Amount</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Order Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Order Information</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Order ID</label>
                    <p className="text-gray-900">#{order?.order_id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Listing Type</label>
                    <p className="text-gray-900 capitalize">{order?.listing_type || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Condition</label>
                    <p className="text-gray-900 capitalize">{order?.listing_condition || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Created</label>
                    <p className="text-gray-900">{formatDate(order?.created_at)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Updated</label>
                    <p className="text-gray-900">{formatDate(order?.updated_at)}</p>
                  </div>
                </div>
              </div>

              {/* Financial Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Financial Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Listing Price</span>
                    <span className="text-sm font-medium">{formatCurrency(order?.listing_price)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Total Amount</span>
                    <span className="text-sm font-bold text-green-600">{formatCurrency(order?.total_amount)}</span>
                  </div>
                  {order?.escrow_amount && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Escrow Amount</span>
                      <span className="text-sm font-medium">{formatCurrency(order.escrow_amount)}</span>
                    </div>
                  )}
                  {order?.escrow_held_at && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Escrow Held Since</label>
                      <p className="text-sm text-gray-900">{formatDate(order.escrow_held_at)}</p>
                    </div>
                  )}
                  {order?.escrow_released_at && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Escrow Released</label>
                      <p className="text-sm text-gray-900">{formatDate(order.escrow_released_at)}</p>
                    </div>
                  )}
                  {order?.escrow_refunded_at && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Escrow Refunded</label>
                      <p className="text-sm text-gray-900">{formatDate(order.escrow_refunded_at)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            {order?.listing_description && (
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Listing Description</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{order.listing_description}</p>
                </div>
              </div>
            )}
          </div>

          {/* Parties Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Parties Involved</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Buyer Information */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">👤 Buyer Information</h4>
                <div className="space-y-2">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Name</label>
                    <p className="text-gray-900">
                      {order?.buyer_first_name} {order?.buyer_last_name}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-gray-900">{order?.buyer_email}</p>
                  </div>
                  {order?.buyer_phone && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Phone</label>
                      <p className="text-gray-900">{order.buyer_phone}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Seller Information */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">🏪 Seller Information</h4>
                <div className="space-y-2">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Store Name</label>
                    <p className="text-gray-900">{order?.seller_store_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Seller Name</label>
                    <p className="text-gray-900">
                      {order?.seller_first_name} {order?.seller_last_name}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-gray-900">{order?.seller_email}</p>
                  </div>
                  {order?.seller_phone && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Phone</label>
                      <p className="text-gray-900">{order.seller_phone}</p>
                    </div>
                  )}
                  {order?.seller_location && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Location</label>
                      <p className="text-gray-900">{order.seller_location}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Timeline / Activity */}
          {order?.timeline && order.timeline.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Order Timeline</h3>
              <div className="space-y-4">
                {order.timeline.map((activity, index) => (
                  <div key={index} className="flex space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-sm">📝</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{activity.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(activity.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - 1/3 width */}
        <div className="space-y-6">
          {/* Admin Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => navigate(`/admin/users/${order?.buyer_id}`)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                👤 View Buyer Profile
              </button>
              <button
                onClick={() => navigate(`/admin/sellers/${order?.seller_id}`)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                🏪 View Seller Profile
              </button>
              <button
                onClick={() => navigate(`/admin/listings/${order?.listing_id}`)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                📦 View Listing
              </button>
              <button
                onClick={() => navigator.clipboard.writeText(orderId)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                📋 Copy Order ID
              </button>
            </div>
          </div>

          {/* Admin Notes */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Admin Notes</h3>
            {order?.admin_notes ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800 whitespace-pre-wrap">{order.admin_notes}</p>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No admin notes yet</p>
            )}
          </div>

          {/* Order Status History */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Status History</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Current Status</span>
                {getStatusBadge(order?.status)}
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Created</span>
                <span className="text-sm text-gray-900">{formatDate(order?.created_at)}</span>
              </div>
              {order?.shipped_at && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Shipped</span>
                  <span className="text-sm text-gray-900">{formatDate(order.shipped_at)}</span>
                </div>
              )}
              {order?.delivered_at && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Delivered</span>
                  <span className="text-sm text-gray-900">{formatDate(order.delivered_at)}</span>
                </div>
              )}
              {order?.completed_at && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Completed</span>
                  <span className="text-sm text-gray-900">{formatDate(order.completed_at)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Update Order Status</h3>
            
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            >
              <option value="">Select Status</option>
              <option value="pending">Pending</option>
              <option value="offer_pending">Offer Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
            </select>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setNewStatus('');
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={!newStatus || actionLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Add Admin Notes</h3>
            
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter admin notes..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
              rows="4"
            />
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNotesModal(false);
                  setNotes('');
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddNotes}
                disabled={!notes.trim() || actionLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Adding...' : 'Add Notes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dispute Resolution Modal */}
      {showDisputeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Resolve Dispute</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Type</label>
                <select
                  value={disputeResolution.resolution}
                  onChange={(e) => setDisputeResolution(prev => ({
                    ...prev,
                    resolution: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Resolution</option>
                  <option value="refund_buyer">Full Refund to Buyer</option>
                  <option value="release_seller">Release Funds to Seller</option>
                  <option value="partial_refund">Partial Refund</option>
                </select>
              </div>

              {disputeResolution.resolution === 'partial_refund' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Refund Amount</label>
                  <input
                    type="number"
                    value={disputeResolution.refund_amount}
                    onChange={(e) => setDisputeResolution(prev => ({
                      ...prev,
                      refund_amount: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="Enter refund amount"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Order total: {formatCurrency(order?.total_amount)}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Notes</label>
                <textarea
                  value={disputeResolution.resolution_notes}
                  onChange={(e) => setDisputeResolution(prev => ({
                    ...prev,
                    resolution_notes: e.target.value
                  }))}
                  placeholder="Enter resolution notes..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowDisputeModal(false);
                  setDisputeResolution({
                    resolution: '',
                    refund_amount: 0,
                    resolution_notes: ''
                  });
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDisputeResolution}
                disabled={!disputeResolution.resolution || actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Resolving...' : 'Resolve Dispute'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderDetail;